import { useContext, useEffect } from "react";
import { Box, Grid, Typography, FormControl, InputLabel, Select, MenuItem, TextField, Stack, Button, Checkbox, Dialog } from "@mui/material"
import { SnakeLoader } from "../../../../../core/loader/SnakeLoader";
import { ToolContext } from "../../../../../core/context/ToolContext";
import { ToolTip } from "../../../../../core/global/ToolTip";
import { rqx } from "../../../../../core/request/API";
import { env } from "../../../../../core/Env";

export const PropertyLocation = ({ handleChange }) => {
    const { realproperty_state } = useContext(ToolContext);
    const { dataLocations, confirmation, types, otherInfrmtn } = realproperty_state;

    const getProvince = async () => {
        if (dataLocations.data.provinces.length === 0) {
            try {
                const res_province = await rqx.get(`<<global>>/${env()}/location/province`, { ref: 'id', filter: 'all' });
                if (res_province) {
                    dataLocations.set((p) => ({
                        ...p, provinces: res_province || [],
                    }));
                }
            } catch (e) {
                console.error('Failed to fetch provinces:', e);
            }
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            if (types.data.indoorFacilities.length === 0 && types.data.outdoorFacilities.length === 0) {
                try {
                    await getProvince();
                    const res_fclts = await rqx.get(`<<tool>>/${env()}/propertyListing/initialReader`, { ref: "facilities" });
                    if (res_fclts) {
                        types.set((p) => ({
                            ...p,
                            indoorFacilities: res_fclts?.filter(v => v.type === 1).sort((a, b) => a.name.localeCompare(b.name)),
                            outdoorFacilities: res_fclts?.filter(v => v.type === 2).sort((a, b) => a.name.localeCompare(b.name)),
                        }));
                    }
                } catch (error) {
                console.error("Failed to fetch facilities or locations:", error);
                }
            }
        };
        fetchData();
        // eslint-disable-next-line
    }, [])

    const handleCloseDialog = (isOpen) => {
		confirmation.set((p) => ({...p, [isOpen]: false}))
	}

    const handleOpenDialog = async (facilities, isOpen, loader) => {
        confirmation.set((p) => ({...p, [isOpen]: true, [loader]: true}));
        if (types.data[facilities].length === 0) {
            const res_fclts = await rqx.get(`<<tool>>/${env()}/propertyListing/initialReader`, { ref: "facilities" });
            if (res_fclts) {
                types.set((p) => ({
                    ...p,
                    indoorFacilities: res_fclts?.filter(v => v.type === 1),
                    outdoorFacilities: res_fclts?.filter(v => v.type === 2),
                }));
                confirmation.set((p) => ({...p, [loader]: false}));
            }
        } else {
            confirmation.set((p) => ({...p, [loader]: false}));
        }
	}

    const handleChangeFacilities = (e, id, fac) => {
        otherInfrmtn.set((p) => {
            const updateFac = e.target.checked ?  [...(p[fac].value || []), id] : (p[fac].value || []).filter((v) => v !== id)
            return {
                ...p,
                [fac]: {
                    ...p[fac],
                    value: updateFac,
                    stat: false,
                    msg: "",
                }
            }
        });
    }

    const rndrFclts = (f) => {
        const fclts = Object.fromEntries(types.data[f]?.map((d) => [d.id, d.name]));
        return otherInfrmtn.data[f].value?.map((v, i) => (
            <Grid item key={i} xs={12} sm={6} md={4} lg={3}>
                <Box display="flex" flexDirection="row" alignItems="center" sx={{ boxShadow: "rgba(0, 0, 0, 0.1) 0px 2px 6px 0px", borderRadius: "8px", bgcolor: "#ffffff" }}>
                    <Checkbox size="small" onChange={(e) => handleChangeFacilities(e, v, f)} checked={otherInfrmtn.data[f].value?.includes(v)}/>
                    <Typography variant="subtitle2">{fclts[v]}</Typography>
                </Box>
            </Grid>
        ));
    }

    const rndrDialog = (open, loader, fcltsTxt, fclts ) => {
        return <Dialog open={confirmation.data[open]} onClose={()=> handleCloseDialog(open)} sx={{ "& .MuiDialog-paper": { backgroundColor: "#f0f4f8", p: "1.5rem" } }}>
            {confirmation.data[loader] ? 
                <Box display="flex" alignItems="center" justifyContent="center">
                        <SnakeLoader bg="#11783C" size="8px" distance="7px" /> 
                </Box>
                :
                <>
                    <Typography textAlign="center" sx={{ fontWeight: "600" }}>Select the applicable {fcltsTxt} facilities.</Typography>
                    <Grid container spacing={2} sx={{ height: "80vh", overflow: "scroll", mt: "0.5rem" }}> 
                        {types.data[fclts]?.map((v) => (
                            <Grid item key={v.id} xs={12} sm={6} md={4}>
                                <Box display="flex" flexDirection="row" alignItems="center" sx={{ boxShadow: "rgba(0, 0, 0, 0.1) 0px 2px 6px 0px", borderRadius: "8px", bgcolor: "#ffffff" }}>
                                    <Checkbox size="small" inputProps={{ name: fclts }} onChange={(e) => handleChangeFacilities(e, v.id, fclts)} checked={otherInfrmtn.data[fclts].value?.includes(v.id)}/>
                                    <Typography variant="subtitle2">{v.name}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                    <Box display="flex" flexDirection="row" justifyContent="space-evenly" pt="1rem" sx={{ position: "sticky", bottom: 0 }}>
                        <Button variant="contained" onClick={()=> handleCloseDialog(open)}> Confirm </Button>
                    </Box>
                </>
            }
        </Dialog>
    }

	return (
        <>  
            { dataLocations.data.provinces.length === 0 || dataLocations.data.cities.length === 0 || dataLocations.data.barangays.length === 0 || types.data.indoorFacilities.length === 0 || types.data.outdoorFacilities.length === 0 ?
                <Box py="0.5rem" display="flex" alignItems="center" justifyContent="center">
                    <SnakeLoader bg="#11783C" size="7px" distance="7px" /> 
                </Box>
            :
                <>
                    <Grid container spacing={2} mb="1rem">
                        <Grid item xs={12} sm={6} md={6} lg={3}>
                            <Typography mb="0.5rem">
                                Province
                                <Typography component="span" color="error">
                                    *
                                </Typography>
                            </Typography>
                            <FormControl fullWidth size="small" sx={{ bgcolor: "#ffffff" }} error={otherInfrmtn.data.province.stat}>
                                { otherInfrmtn.data.province.value === "" && <InputLabel id="province-select-label" shrink={false}>Select Province</InputLabel>}
                                <Select name="province" id="province-select" value={otherInfrmtn.data.province.value} onChange={(e) => handleChange(e, otherInfrmtn)}>
                                    {dataLocations.data.provinces.length > 0 ? (
                                        dataLocations.data.provinces?.map((p, i) => (
                                            <MenuItem key={i} value={p.id} >
                                                {p.name}
                                            </MenuItem>
                                        ))
                                    ) : (
                                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                            <Typography sx={{ fontSize: "0.8rem", fontStyle: "italic", fontWeight: "600", mr: "1rem", color: "#635f5a" }}>Getting Provinces</Typography>
                                            <SnakeLoader bg="#11783C" size="5px" distance="4px" />
                                        </Box>
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={6} lg={3}>
                            <Typography mb="0.5rem">
                                City
                                <Typography component="span" color="error">
                                    *
                                </Typography>
                            </Typography>
                            <ToolTip disableHoverListener={otherInfrmtn.data.province.value ? true : false} title={"Select a province first"}>
                                <FormControl fullWidth size="small" sx={{ bgcolor: "#ffffff" }} error={otherInfrmtn.data.city.stat}>
                                    { otherInfrmtn.data.city.value === "" && <InputLabel id="city-select-label" shrink={false}>Select City</InputLabel> }
                                    <Select name="city" id="city-select" value={otherInfrmtn.data.city.value} onChange={(e) => handleChange(e, otherInfrmtn)} disabled={!otherInfrmtn.data.province.value}>
                                        {dataLocations.data.cities?.map((c, i) => (
                                            <MenuItem key={i} value={c.id}>
                                                {c.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </ToolTip>
                        </Grid>
                        <Grid item xs={12} sm={6} md={6} lg={3}>
                            <Typography mb="0.5rem"> Barangay </Typography>
                            <ToolTip disableHoverListener={otherInfrmtn.data.city.value ? true : false} title={"Select a city first"}>
                                <FormControl fullWidth size="small" sx={{ bgcolor: "#ffffff" }}>
                                    { otherInfrmtn.data.barangay.value === "" && <InputLabel id="barangay-select-label" shrink={false}>Select Barangay</InputLabel>}
                                    <Select name="barangay" id="barangay-select" value={otherInfrmtn.data.barangay.value} onChange={(e) => handleChange(e, otherInfrmtn)} disabled={!otherInfrmtn.data.city.value}>
                                        {dataLocations.data.barangays?.map((b, i) => (
                                            <MenuItem key={i} value={b.id}>
                                                {b.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </ToolTip>
                        </Grid>
                        <Grid item xs={12} sm={6} md={6} lg={3}>
                            <Typography mb="0.5rem">Street Number</Typography>
                            <TextField name="streetNumber" value={otherInfrmtn.data.streetNumber} onChange={(e) => handleChange(e, otherInfrmtn)} label={ otherInfrmtn.data.streetNumber === "" && "Input Street Number"} variant="outlined" fullWidth size="small" sx={{ bgcolor: "#ffffff" }} InputLabelProps={{ shrink: false }}/>
                        </Grid>
                    </Grid>
                    <Box>
                        <Typography mb="0.5rem" >Indoor Facilities</Typography>
                            <Stack alignItems="center">
                                <Button variant="contained" onClick={() => handleOpenDialog("indoorFacilities", "isIndoorFacOpen", "indoorLoader")}> Add Indoor Facilities </Button>
                            </Stack>
                            { confirmation.data.isIndoorFacOpen && rndrDialog("isIndoorFacOpen", "indoorLoader", "indoor", "indoorFacilities")}
                            <Box>
                                <Grid mb="1rem" container spacing={1.5} sx={{ maxHeight: "60vh", overflow: "scroll", mt: "0.5rem" }}> 
                                    {rndrFclts("indoorFacilities")}
                                </Grid>
                            </Box>
                        <Typography mb="0.5rem">Outdoor Facilities</Typography>
                            <Stack alignItems="center">
                                <Button variant="contained" onClick={() => handleOpenDialog("outdoorFacilities", "isOutdoorFacOpen", "outdoorLoader")}> Add Outdoor Facilities </Button>
                            </Stack>
                            { confirmation.data.isOutdoorFacOpen && rndrDialog("isOutdoorFacOpen", "outdoorLoader", "outdoor", "outdoorFacilities")}
                            <Box>
                                <Grid mb="1rem" container spacing={1.5} sx={{ maxHeight: "60vh", overflow: "scroll", mt: "0.5rem" }}> 
                                    {rndrFclts("outdoorFacilities")}
                                </Grid>
                            </Box>
                    </Box>
                </>
            }
        </>
	)
}
